<template>
  <v-card>
    <div class="letters">
      <v-card-title>Select letter template</v-card-title>
      <div class="pl-4 pr-4 lettersTree">
        <div class="foldersList">
          <div v-if="loading">
            <v-progress-circular
              :size="20"
              color="primary"
              class="mr-2"
              indeterminate
            ></v-progress-circular>
            Loading...
          </div>
          <v-treeview
            v-else
            class="letterFolder"
            activatable
            hoverable
            dense
            :items="folders"
            item-text="Name"
            item-key="FullPath"
            item-children="Folders"
            @update:active="getLettersTemplates"
          >
            <!-- eslint-disable-next-line -->
            <template v-slot:prepend="{ _, open }">
              <v-icon>
                {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
              </v-icon>
            </template>
          </v-treeview>
        </div>

        <v-divider vertical></v-divider>

        <div class="filesList">
          <div v-if="filesLoading">
            <v-progress-circular
              :size="20"
              color="primary"
              class="mr-2"
              indeterminate
            ></v-progress-circular>
            Loading...
          </div>
          <v-list v-else dense class="pa-0">
            <v-list-item-group color="primary">
              <v-list-item v-for="(file, index) in files" :key="index">
                <v-list-item-content @click="selectTemplate(file)">{{ file }}</v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </div>
      </div>
      <v-card-actions>
        <v-btn v-if="selectedFile" color="primary" outlined @click="downloadTemplate"
          >Download</v-btn
        >
        <v-btn v-if="selectedFile" color="primary" outlined @click="confirmDelete">Delete</v-btn>

        <div class="upload ml-8">
          <v-file-input
            v-model="uploadFile"
            ref="file"
            label="Upload Document"
            accept=".docx"
            dense
          ></v-file-input>
          <v-btn
            class="ml-2"
            :disabled="currentPath == null"
            depressed
            color="primary"
            @click="uploadDocument()"
          >
            Go
          </v-btn>
        </div>
      </v-card-actions>
    </div>
    <Dialog ref="confirmationDialog" v-model="dialog"></Dialog>
  </v-card>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';

export default {
  components: { Dialog },
  name: 'LettersLibrary',
  data() {
    return {
      loading: false,
      filesLoading: false,
      folders: [],
      files: [],
      currentPath: null,
      selectedFile: null,
      uploadFile: null,
      dialog: false,
    };
  },

  computed: {
    downloadPath() {
      if (this.currentPath && this.selectedFile) return this.currentPath + '/' + this.selectedFile;
      return null;
    },
  },

  mounted() {
    this.getLetterFolders();
  },

  methods: {
    async uploadDocument() {
      try {
        let data = new FormData();
        data.append('Path', this.currentPath);
        data.append('File', this.uploadFile);
        await this.$api({
          url: '/Letters/UploadDocument',
          method: 'POST',
          data: data,
        });

        this.files.push(this.uploadFile.name);
        this.uploadFile = null;
      } catch (error) {
        console.error(error);
        this.$store.commit('setNotificationMessage', { type: 'error', text: error.message });
      }
    },
    async confirmDelete() {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this template ?'
        );
        await this.deleteTemplate();
      } catch (err) {
        console.error(err);
      }
    },
    async deleteTemplate() {
      try {
        await await this.$api.delete('/Letters/DeleteLetterTemplate', {
          params: {
            currentPath: this.currentPath,
            fileName: this.selectedFile,
          },
        });
        var index = this.files.indexOf(this.selectedFile);
        this.files.splice(index, 1);
      } catch (error) {
        console.error(error);
        this.$store.commit('setNotificationMessage', { type: 'error', text: error.message });
      }
    },
    selectTemplate(file) {
      this.selectedFile = file;
    },

    async getLetterFolders() {
      this.loading = true;
      try {
        const response = await this.$api.get('/Letters/GetLetterFolders');

        if (!response.data) {
          const msg = 'Unable to load letters folders';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        this.folders = response.data.Folders;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async getLettersTemplates(paths) {
      this.filesLoading = true;
      const path = paths[0];
      if (!path) {
        this.filesLoading = false;
        return;
      }
      this.currentPath = path;
      try {
        const response = await this.$api.get(
          '/Letters/GetLetterTemplates?path=' + encodeURIComponent(path)
        );

        if (!response.data) {
          const msg = 'Unable to load letters templates';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        this.files = response.data.Files;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.filesLoading = false;
      }
    },
    async downloadTemplate() {
      try {
        const response = await this.$api({
          method: 'GET',
          url: '/Letters/DownloadTemplate?filePath=' + encodeURIComponent(this.downloadPath),
          responseType: 'blob',
        });

        if (!response.data) {
          const msg = 'Unable to get download path';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }
        console.log(response);
        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            //type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            type: 'application/msword',
          })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.selectedFile);
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        console.log(error.message);
      }
    },
  },
};
</script>

<style lang="scss">
.letters {
  height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;

  .lettersTree {
    display: grid;
    grid-template-columns: auto auto 1fr;
    column-gap: 15px;
  }

  .filesList {
    overflow-y: auto;
  }
  .upload {
    width: 600px;
    display: flex;
  }

  .letterFolder {
    cursor: pointer;
  }
}
</style>
